import { create } from "zustand";

/* -------------------- Types -------------------- */

export interface InformantDetails {
  title?: string;
  firstName?: string;
  lastName?: string;
  initials?: string;
  gender?: string;
  dateOfBirth?: string;
  maritalStatus?: string;
  nationality?: string;
  countryOfBirth?: string;
  countryOfResidence?: string;
  beneficiaryEmployer?: string;
  address?: string;
  contactNumber?: string;
  countryCode?: string;
  email?: string;
  branchName?: string;
  postalAddress?: string;
  employerName?: string;
  occupation?: string;
  sourceOfFunds?: string;
  identificationNumber?: string;
  postalCode?: string;
  agentOrBroker?: string;
  identificationType?: string;
  networkProvider?: string;
  relationship?: string;
  whatsappNotifications?: boolean;
  smsNotifications?: boolean;
}

export interface DeceasedDetails {
  title?: string;
  initials?: string;
  firstName?: string;
  lastName?: string;
  gender?:string;
  relationship?: string;
  identificationNumber?: string;
  identificationType?: string;
  email?: string;
  contactNumber?: string;
  dateOfDeath?: string;
  dateOfBirth?: string;
  causeOfDeath?: string;
  postalCode?:string;
}

export interface DeceasedLocation {
  houseNumber?: string;
  streetName?: string;
  suburb?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  locationType?: string;
  notes?: string;
}

export interface CaseDocuments {
  death_certificate?: File | null;
  dha_1680_home_death_form?: File | null;
  deceased_id?: File | null;
  claimant_id?: File | null;
  dha_1663_form?: File | null;
  bank_statement?: File | null;
  signature_of_claimant?: File | null;
  other_document?: File | null;
}

export interface FuneralPackageItem {
  itemName?: string;
  quantity?: string;
  notes?: string;
}

export interface FuneralPackageSection {
  sectionName?: string;
  warehouseRelevant?: boolean;
  items?: FuneralPackageItem[];
}

export interface QuoteLineItem {
  code: string;
  name: string;
  kind?: string;
  quantity: number;
  unitPrice: number;
  taxCode?: string;
}

export interface CasePackageSelection {
  sourceType?: "MANUAL" | "POLICY";
  funeralCategoryCode?: string;
  funeralCategoryName?: string;
  funeralPackageCode?: string;
  funeralPackageName?: string;
  funeralPackageVersion?: number;
  summary?: string;
  sections?: FuneralPackageSection[];
  lineItems?: QuoteLineItem[];
}

export interface LivesInsuredSelection {
  [key: string]: any;
}

/* -------------------- Store -------------------- */

interface CaseCreationState {
  informantDetails: InformantDetails;
  deceasedDetails: DeceasedDetails;
  deceasedLocation: DeceasedLocation;
  mortuaryName: string;
  documents: CaseDocuments;
  packageSelection: CasePackageSelection;
  policyNumber: string;
  selectedLivesInsured: LivesInsuredSelection | null;

  setInformantDetails: (data: Partial<InformantDetails>) => void;
  setDeceasedDetails: (data: Partial<DeceasedDetails>) => void;
  setDeceasedLocation: (data: Partial<DeceasedLocation>) => void;
  setMortuaryName: (mortuaryName: string) => void;
  setDocuments: (data: Partial<CaseDocuments>) => void;
  setPackageSelection: (data: Partial<CasePackageSelection>) => void;
  setPolicyNumber: (policyNumber: string) => void;
  setSelectedLivesInsured: (data: LivesInsuredSelection | null) => void;
  resetPackageSelection: () => void;
  resetDocuments: () => void;

  resetCase: () => void;
}

export const useCaseCreationStore = create<CaseCreationState>((set) => ({
  informantDetails: {
     postalCode:"postalCode",
     identificationType:"id",
     countryCode: "+27",
  },
  deceasedDetails: {
    postalCode:"postalCode",
    identificationType:"id"
  },
  deceasedLocation: {},
  mortuaryName: "",
  documents: {},
  packageSelection: {},
  policyNumber: "",
  selectedLivesInsured: null,

  setInformantDetails: (data) =>
    set((state) => ({
      informantDetails: { ...state.informantDetails, ...data },
    })),

  setDeceasedDetails: (data) =>
    set((state) => ({
      deceasedDetails: { ...state.deceasedDetails, ...data },
    })),

  setDeceasedLocation: (data) =>
    set((state) => ({
      deceasedLocation: { ...state.deceasedLocation, ...data },
    })),

  setMortuaryName: (mortuaryName) => set({ mortuaryName }),

  setDocuments: (data) =>
    set((state) => ({
      documents: { ...state.documents, ...data },
    })),

  setPackageSelection: (data) =>
    set((state) => ({
      packageSelection: { ...state.packageSelection, ...data },
    })),

  setPolicyNumber: (policyNumber) =>
    set({
      policyNumber,
    }),

  setSelectedLivesInsured: (data) =>
    set({
      selectedLivesInsured: data,
    }),

  resetPackageSelection: () =>
    set({
      packageSelection: {},
    }),

  resetDocuments: () =>
    set({
      documents: {},
    }),

  resetCase: () =>
    set({
      informantDetails: {
        identificationType: "id",
        countryCode: "+27",
      },
      deceasedDetails: {},
      deceasedLocation: {},
      mortuaryName: "",
      documents: {},
      packageSelection: {},
      policyNumber: "",
      selectedLivesInsured: null,
    }),
}));
